import { useEffect } from "react";
import { TopBar } from "./kit";
import LogStepEyebrow from "./LogStepEyebrow";
import LogMatchGroundStep from "./LogMatchGroundStep";
import LogMatchAddGroundPanel from "./LogMatchAddGroundPanel";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
// Issue #757 review round 2, R1 (Andreas's decision, 2026-09-24) — the
// SAME three components `Layout.jsx`'s shared header uses, reused verbatim
// (not forked): this screen supplies its own navy `TopBar` rather than the
// shared white header (`Layout.jsx`'s own comment on `ownsPageByScreen`
// explains why restyling can't get there), but it must still offer the
// same capabilities — notifications, sign-out and the contrast toggle — a
// suppressed shared header would otherwise take away. Round 5 conformance
// (D56, DirectionA1.dc.html/FirstGround.dc.html `topbar-notifications`)
// added the bell; `NotificationsBell` self-gates on role (player/coach
// only), same as `Layout.jsx`'s own call site.
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";

/**
 * Issue #757 review round 2 (B1/B8) — step 1's OWN page surface, extracted
 * out of `PerformanceTracking.jsx` so that file stays the flow only (#749's
 * extraction brief) and this component owns the rendering: the drawn navy
 * `TopBar` (`DirectionA1.dc.html`/`FirstGround.dc.html`), the step
 * counter, and which of the two sub-screens (the list, or the reduced
 * add-a-ground panel) is showing.
 *
 * Mounted ONLY while `PerformanceTracking.jsx`'s own `showLogGroundStep1`
 * is true — never conditionally re-rendered with it false, so the mount/
 * unmount pair below is exactly the "screen is on screen" signal
 * `ChromelessOverrideContext`/`RetireTextSizeContext` both ask for:
 * `active`/`retired` go true the instant this mounts and false the instant
 * it unmounts (`PerformanceTracking.jsx` stops rendering this component
 * entirely once the ground resolves or the flow closes), which is what
 * makes "the shipped page renders nothing while this is up" and "the
 * text-size control is retired for exactly this duration" both true by
 * construction rather than by a comment.
 */
export default function LogGroundFlow({ logStep, addGroundSeed, presetGround, onPicked, onAddGround, onAddGroundSaved, onCancelAddGround, onExitStep1 }) {
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // Round 5 conformance (token-check) — an explicit, declared `color`
    // at the root, not left to inherit the app shell's own `#0B1220`-ish
    // default: every plain layout wrapper under this mount (the topbar
    // div itself, the content stack, the saved-list/map-band containers,
    // the add-ground panel, both sr-only markers) has no text of its own
    // and was inheriting that default, which the round-5 token set
    // (tokens-round-5.json, read from the drawings) never declared —
    // `text_main: #0F172A` is exactly `--nw-ink`, and matches the drawn
    // `body { color: #0F172A }` both artboards declare.
    <div data-testid="log-ground-flow" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="navy"
        // PR #887 review, B4 — `topbar-logo` + the home link; player-only
        // screen, home is always `/dashboard`.
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        // Review round 2, R1 — text size stays retired on this step
        // (measured static, `useRetireTextSize` above); `hideTextSize`
        // passed directly rather than derived from the route, since this
        // instance is never reached through `Layout.jsx`'s own header at
        // all (that header is suppressed for exactly this mount — see
        // `Layout.jsx`'s comment on `ownsPageByScreen`).
        endControls={
          <>
            <NotificationsBell compact />
            <AccessibilityMenu hideTextSize />
            {/* #780 (D140) — the drawn empty account circle; see AccountMenu.jsx's own docstring on the `empty` prop. */}
            <AccountMenu empty />
          </>
        }
        data-testid="log-step-1-topbar"
      />
      <LogStepEyebrow step={1} total={3} />
      {logStep === "add-ground" ? (
        <LogMatchAddGroundPanel seed={addGroundSeed} onSaved={onAddGroundSaved} onCancel={onCancelAddGround} />
      ) : (
        <LogMatchGroundStep presetGround={presetGround} onPicked={onPicked} onAddGround={onAddGround} onExitStep1={onExitStep1} />
      )}
    </div>
  );
}
